import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import { Box, Button, Menu, MenuItem, SxProps, Typography } from '@mui/material';
import { useState } from 'react';
import { getNativeBridgeFunction } from '../../../commonUtils/nativeBridge';
import { pxToRem, pxTovW } from '../../../commonUtils/resizeUtils';
import { IStyles } from '../../../commonUtils/styleUtils';

const styles: IStyles = {
  trigger: {
    textTransform: 'none',
    maxWidth: '100%',
    justifyContent: 'space-between',
    gap: { xs: pxToRem(6), md: pxTovW(8) },
    paddingX: { xs: pxToRem(12), md: pxTovW(16) },
    paddingY: { xs: pxToRem(4), md: pxTovW(6) },
    borderRadius: { xs: pxToRem(8), md: pxTovW(10) },
  },
  triggerLabel: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  menuItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: { xs: pxToRem(2), md: pxTovW(2) },
    paddingY: { xs: pxToRem(8), md: pxTovW(10) },
    whiteSpace: 'normal',
  },
};

export interface ResourceListDropdownItem {
  resourceId: string;
  title: string;
  /** Resource type or category, shown under the title to tell entries apart. */
  subtitle?: string;
  /** Absolute URL the resource opens at. */
  href: string;
}

interface IProps {
  resources: ResourceListDropdownItem[];
  /** The resource already on screen - listed, but not offered as a link. */
  currentResourceId?: string;
  label?: string;
  sx?: SxProps;
}

/**
 * The project's resources as a dropdown, each one opening the resource in its
 * own tab - or the device browser inside the app's WebView. Not router
 * navigation, so the tab the reader is working in - a practical's editor above
 * all - is left alone.
 */
export const ResourceListDropdown = (props: IProps) => {
  const { resources, currentResourceId, label, sx } = props;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  if (!resources.length) {
    return null;
  }

  const openResource = (url: string) => {
    const redirectToBrowser = getNativeBridgeFunction('redirectToBrowser');
    if (redirectToBrowser) {
      try {
        redirectToBrowser(url);
      } catch (error) {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
    setAnchorEl(null);
  };

  return (
    <Box sx={sx}>
      <Button
        variant="outlined"
        color="primary"
        onClick={(event) => setAnchorEl(event.currentTarget)}
        endIcon={<KeyboardArrowDownRoundedIcon />}
        sx={styles.trigger}
      >
        <Typography variant="bodyText" sx={styles.triggerLabel}>
          {label || 'Project Resources'}
        </Typography>
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        {resources.map((resource) => {
          const isCurrent = resource.resourceId === currentResourceId;
          return (
            <MenuItem
              key={resource.resourceId}
              disabled={isCurrent}
              selected={isCurrent}
              onClick={() => openResource(resource.href)}
              sx={styles.menuItem}
            >
              <Typography variant="bodyText" fontWeight="bold">
                {resource.title}
              </Typography>
              {resource.subtitle && (
                <Typography variant="smallText" color="text.secondary">
                  {resource.subtitle}
                </Typography>
              )}
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
};
